/**
 * Нижняя шторка — основной способ показать детали и подтвердить действие.
 *
 * Диалоги Telegram (showConfirm) выглядят системно и вырывают из приложения,
 * поэтому подтверждения делаем своей шторкой. Исключение — по-настоящему
 * необратимые действия, где системный диалог уместен как «стоп-кран».
 */

import clsx from 'clsx'
import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // фон не должен прокручиваться под открытой шторкой
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="absolute inset-0 bg-black/35"
        style={{ animation: 'fade-in 180ms var(--ease-out-soft) both' }}
      />
      <div
        className={clsx(
          'bg-surface relative mx-auto w-full max-w-[520px] rounded-t-[var(--radius-sheet)]',
          'shadow-[var(--shadow-sheet)] ring-1 ring-[color-mix(in_srgb,var(--color-line)_60%,transparent)]',
          'pb-safe max-h-[88vh] overflow-y-auto',
        )}
        style={{ animation: 'sheet-up 320ms var(--ease-spring) both' }}
      >
        <div className="bg-surface sticky top-0 z-10 flex justify-center pt-3 pb-1.5">
          <span className="bg-line h-1 w-11 rounded-full" />
        </div>
        {title && <h2 className="font-display px-5 pt-1.5 pb-1 text-title leading-tight">{title}</h2>}
        <div className="px-5 pt-2 pb-4">{children}</div>
        {footer && <div className="border-line bg-surface sticky bottom-0 border-t px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Подтверждение опасного действия (17.13: необратимое требует подтверждения).
 */
export function ConfirmSheet({
  open,
  title,
  description,
  confirmLabel = 'Подтвердить',
  cancelLabel = 'Отмена',
  danger = false,
  loading = false,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {description && <p className="text-text-muted text-body leading-snug">{description}</p>}
      <div className="mt-5 space-y-2">
        <Button
          block
          size="lg"
          variant={danger ? 'danger' : 'primary'}
          loading={loading}
          hapticKind={danger ? 'select' : 'tap'}
          onClick={() => {
            if (danger) haptic('warning')
            onConfirm()
          }}
        >
          {confirmLabel}
        </Button>
        <Button block size="lg" variant="ghost" onClick={onClose} hapticKind="none">
          {cancelLabel}
        </Button>
      </div>
    </Sheet>
  )
}
